/*
 * ****************************************************************************
 * Copyright (C) 2018-2018, All rights reserved.
 * Project: chessmate
 * Created On: March 23rd, 2018
 * Last Modified: March 23rd, 2018
 * ****************************************************************************
 */

import * as React from 'react';
import * as Mui from 'mui';
import { PlayChessProps } from '..';
import { ChessGame, PopupMenuItem } from 'components';
import { Dimensions } from 'components/board/chessTypes';
import { breakpoints } from 'app/breakpoints';
import { GameStateContextProvider, GameState } from 'contexts/gameStateContext';
import { GameHistory } from './gameHistory';
import * as deepEqual from 'deep-equal';
import { GameControlBar } from './gameControlBar';
import { AppEvents } from 'contexts/appEventsContext';
import { PiecesLost } from './piecesLost';
import renderChessPiece from 'app/renderChessPiece';
import { Clipboard } from 'components/clipboard';
import { Move } from '@chess-fu/chess-game';
import { FenParser } from '@chess-fu/fen-parser';

/** PlayGameProps - Properties */
export interface PlayGameProps extends PlayChessProps {
  bounds: Dimensions;
  game: GameState;
  events: AppEvents;
}

export interface GameUriOptions {
  side: 'w' | 'b';
  pos?: number;
}

export interface PlayGameState {
  moveOrdinal: number;
  currentUri: string;
  options: GameUriOptions;
  unsubscribe?: () => void;
}

/** PlayGame */
export class PlayGame extends React.Component<PlayGameProps, PlayGameState> {
  constructor(props: PlayGameProps, ctx: any) {
    super(props, ctx);
    this.state = {
      currentUri: '',
      moveOrdinal: 0,
      options: { side: 'w' },
    };
  }

  componentDidMount() {
    const subscriptions = [
      this.props.game.onChange((arg) => this.onGameChange(arg)),
      this.props.events.onMenu('new-game', this.edit),
      this.props.events.onBuildMenu(this.buildMenu),
    ];

    this.setState({
      unsubscribe: () => { subscriptions.forEach(fn => fn()); }
    });
  }

  componentWillUnmount() {
    if (this.state.unsubscribe) {
      this.state.unsubscribe();
    }
  }

  onGameChange(arg: { position: number }) {
    if (this.state.moveOrdinal !== arg.position) {
      console.info('changed:', arg);
      this.setState({ moveOrdinal: arg.position });
    }
  }

  readonly edit = () => {
    const fenUrl = '/setup/' + this.props.game.fen();
    this.props.history.push(fenUrl);
  }

  readonly onPaste = (data: { value: string }) => {
    const fen = data.value;
    if (FenParser.isFen(fen)) {
      const fenUrl = this.props.match.path + fen.replace(/ /g, '.');
      this.props.history.push(fenUrl);
    }
    else {
      try { this.props.game.loadPgn(fen); }
      catch (ex) {
        console.warn('bad game:', { fen });
        alert(ex.message);
      }
      const side = new FenParser(this.props.game.startingFEN()).turn || 'w';
      const uriState = this.props.game.toUriState({ side });
      console.log(uriState);
      this.props.history.push(this.props.match.path + GameState.toUri(uriState));
    }
  }

  readonly flipBoard = () => {
    const options = { ...this.state.options };
    options.side = options.side === 'w' ? 'b' : 'w';
    this.setState({ options });

    const uriData = this.props.game.toUriState(options);
    const url = this.props.match.path + GameState.toUri(uriData);
    this.props.history.push(url);
  }

  readonly buildMenu = (menu: PopupMenuItem[]) => {
    menu.push(
      { icon: 'edit', text: 'Setup board', action: this.edit },
      { icon: 'library_books', text: 'Copy PGN', action: this.props.events.menuClick('copy') },
      { icon: 'assignment_returned', text: 'Paste FEN/PGN', action: this.props.events.menuClick('paste') }
    );
  };

  readonly optionsMenu: PopupMenuItem[] = [
    { icon: 'swap_vert', text: 'Flip Board', action: this.flipBoard },
  ];

  static getDerivedStateFromProps(props: PlayGameProps, state: PlayGameState) {
    const { pathname, search } = props.location;
    const path = pathname.substr(props.match.path.length) || 'start';
    const currentUri = `${path}?${search}`;
    if (currentUri === state.currentUri) {
      return null;
    }

    const current = props.game.toUriState(state.options);
    const uriData = GameState.fromUri(path, props.location.search);
    if (!deepEqual(uriData, current)) {
      props.game.setGameState(uriData);
    }

    const stateChange: any = { currentUri };
    if (!deepEqual(uriData.options, current.options)) {
      stateChange.options = { ...state.options, ...uriData.options };
    }

    return stateChange;
  }

  readonly onMovePiece = (move: Move): Promise<Move> | Move => {
    if (move.promotion) {
      move = { ...move, promotion: 'q' };
    }

    const { game } = this.props;
    move = this.props.game.move(move);

    const uriData = game.toUriState(this.state.options);
    const url = this.props.match.path + GameState.toUri(uriData);
    this.props.history.push(url);
    return move;
  }

  render() {
    const { bounds, game } = this.props;
    const { side } = this.state.options;

    let hasHistory = bounds.width >= breakpoints.sm;
    const szPiecesBar = bounds.width >= breakpoints.sm ? 48 : 40;
    const szAvailable = {
      height: bounds.height - 2 - 48,
      width: bounds.width - 2 - szPiecesBar - (hasHistory ? 300 : 0),
    };

    let horzMargin = bounds.width >= breakpoints.sm ? 16 : 0;
    let vertMargin = bounds.height >= breakpoints.sm ? 16 : 0;

    const minSizeXY = Math.floor(Math.min(szAvailable.width - (horzMargin * 2), szAvailable.height - (vertMargin * 2)));
    const boardSize = { height: minSizeXY, width: minSizeXY };

    if ((szAvailable.width - boardSize.width) >= 300) {
      hasHistory = true;
    }
    if ((szAvailable.width - boardSize.width) >= 32) {
      horzMargin = 16;
    }
    if ((szAvailable.height - boardSize.height) >= 32) {
      vertMargin = 16;
    }

    let highlightMoves: any = undefined;
    if (game.position > 0) {
      const lastMove = game.getMove(game.position - 1);
      if (lastMove) {
        highlightMoves = [lastMove];
      }
    }

    const piecesBarStyle = {
      flexBasis: `${szPiecesBar}px`,
      minWidth: `${szPiecesBar}px`,
      height: '100%',
      minHeight: `${szAvailable.height}px`,
      paddingTop: `${vertMargin}px`,
    };

    return (
      <GameStateContextProvider value={this.props.game}>
        <Clipboard events={this.props.events} value={this.props.game.pgn()} onPaste={this.onPaste} />
        <Mui.Paper className="background-color fill column" style={{ margin: `0` }}>
          <div className="flex row no-wrap top">
            <div
              className="chessbar-vert flex column top col-1"
              style={piecesBarStyle}>
              <div style={{ height: minSizeXY }}>
                <PiecesLost
                  flipBoard={side === 'b'}
                  fen={game.fen()}
                  height={minSizeXY}
                  renderPiece={renderChessPiece}
                />
              </div >
              <div className="fill"></div>
              <Mui.IconButton color="inherit"
                style={{ width: `${szPiecesBar}px`, height: `${szPiecesBar}px`, position: 'absolute', bottom: `${48 + vertMargin}px` }}
                onClick={this.flipBoard}>
                <Mui.Icon>swap_vert</Mui.Icon>
              </Mui.IconButton>
            </div>
            <ChessGame
              style={{ margin: `${vertMargin}px ${horzMargin}px` }}
              flipBoard={side === 'b'}
              boardSize={boardSize}
              fen={game.fen()}
              game={this.props.game.game}
              renderPiece={renderChessPiece}
              onMovePiece={this.onMovePiece}
              highlightMoves={highlightMoves}
            />
            {hasHistory && (
              <GameHistory
                className="row fill col-1"
                style={{ minWidth: '200px', height: '100%', minHeight: `${szAvailable.height}px` }}
              />
            )}
          </div>
          <GameControlBar
            leftMargin={szPiecesBar}
            boardWidth={minSizeXY + (horzMargin * 2)}
            optionsMenu={this.optionsMenu}
          />
        </Mui.Paper>
      </GameStateContextProvider>
    );
  }
}

export default PlayGame;


// WEBPACK FOOTER //
// ./src/views/playChess/components/playGame.tsx